/**
 * Events of the maps (GDD section 10). Steam Dome and Nuclear Plant: the vents boil water into steam; the plant's
 * conveyor brings acid barrels. Fort-Pierre: the cauldron tips its burning oil over the gate when a
 * shot breaks it; the powder magazine blows up (radius 40) when fire or lava reaches it. Île de Braise: an eruption
 * every 4 turns, 25 lava bombs and some 200 cells of lava overflowing the crater.
 */
import { Physique, STATE_LIQUID, cosDeg, sinDeg, isqrt, nextInt, wakeRect, TRIG_ONE } from '@invaders/sim';
import { FP_SHIFT, PFP, PFP_SHIFT, TICKS_PER_SECOND, type GameState } from '../state';
import { explode } from '../explosion';
import { PROJ_LAVA_BOMB, WEAPON_ACID_BARREL } from '../weapon-ids';
import { spawnBarrel } from '../barrel';
import { UNIT_H, UNIT_W, boxFree } from '../unit';
import { cauldronIntact } from './fort';
import { ERUPTION_EVERY } from './ember';

const { EMPTY, OIL, FIRE, WOOD, LAVA, WATER, STEAM } = Physique;

/** GDD: the cauldron pours 80 cells of oil, with flames. */
export const CAULDRON_OIL = 80;
/** GDD: the powder magazine's explosion has a radius of 40. Its damage is not in the GDD: tuning value. */
export const MAGAZINE_RADIUS = 40;
export const MAGAZINE_DAMAGE = 180; // 60, tripled like every powder explosion (author's choice)
/** GDD: an eruption throws 25 lava bombs and about 200 cells of lava overflow the crater. */
export const LAVA_BOMBS = 25;
export const ERUPTION_OVERFLOW = 200;
/** Cells tried per tick in each vent's box (the water among them turns into steam). */
const VENT_TRIES = 14;
/** The conveyor's barrels: one per turn (every BELT_SANDBOX_TICKS in the sandbox), at most this many of them around. */
export const BELT_BARRELS = 4;
export const BELT_SANDBOX_TICKS = 8 * TICKS_PER_SECOND;
/** They hold a third of a thrown barrel. */
export const BELT_LOAD = 400;

/** Empty or gas: where poured matter may go. */
function free(state: GameState, i: number): boolean {
  return state.world.rules.materials.state[state.world.physique.data[i]] > STATE_LIQUID;
}

/** After the step, before the reaction blasts are turned into explosions. */
export function updateMapEvents(state: GameState): void {
  const mp = state.map;
  if (mp.cauldron && !cauldronIntact(state.world.physique.data, state.world.physique.w, mp.cauldron)) tipCauldron(state);
  if (mp.magazine) checkMagazine(state);
  if (mp.vents) boil(state);
  if (mp.belts.length > 0) runBelts(state);
}

/** The vents: the water in their boxes turns into steam, a few cells per tick. */
function boil(state: GameState): void {
  const { w, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const rng = state.world.rng;
  for (const v of state.map.vents!) {
    for (let k = 0; k < VENT_TRIES; k++) {
      const i = (v.y0 + nextInt(rng, v.y1 - v.y0 + 1)) * w + v.x0 + nextInt(rng, v.x1 - v.x0 + 1);
      if (data[i] === WATER) { data[i] = STEAM; cells[i] = 0; }
    }
    wakeRect(state.world, v.x0, v.y0, v.x1, v.y1);
  }
}

/**
 * The conveyors carry the units standing on them (the barrels: barrel.ts), one belt at most per unit (the first in the
 * list); in the sandbox, those that bring barrels bring them.
 */
function runBelts(state: GameState): void {
  const world = state.world;
  for (const u of state.units) {
    if (!u.alive) continue;
    const ux = u.x >> FP_SHIFT;
    const feet = (u.y >> FP_SHIFT) + UNIT_H;
    for (const belt of state.map.belts) {
      if (feet !== belt.y || ux + UNIT_W <= belt.x0 || ux > belt.x1) continue;
      const nx = u.x + belt.dir * belt.speed;
      if (boxFree(world, nx >> FP_SHIFT, u.y >> FP_SHIFT)) u.x = nx;
      break;
    }
  }
  if (state.match === null && state.tick % BELT_SANDBOX_TICKS === 0) beltBarrels(state);
}

/** A new barrel at the head of each conveyor that brings them, unless too many of their barrels are around. */
function beltBarrels(state: GameState): void {
  for (const belt of state.map.belts) {
    if (!belt.barrels || state.barrels.filter((b) => b.owner < 0).length >= BELT_BARRELS) continue;
    const head = belt.dir > 0 ? belt.x0 + 8 : belt.x1 - 8;
    spawnBarrel(state, WEAPON_ACID_BARREL, head, belt.y - 1, belt.dir * belt.speed, -1, BELT_LOAD);
  }
}

/** At the start of every turn (GDD step 1, with the wind, the chest and the legacies). */
export function mapTurnStart(state: GameState): void {
  const mp = state.map;
  beltBarrels(state);
  if (!mp.crater) return;
  mp.eruptionIn--;
  if (mp.eruptionIn <= 0) {
    erupt(state);
    mp.eruptionIn = ERUPTION_EVERY;
  }
}

/** The cauldron's oil pours down the tower's face onto the gate, burning; flames catch the drawbridge. */
export function tipCauldron(state: GameState): void {
  const c = state.map.cauldron;
  const gate = state.map.gate;
  if (!c) return;
  state.map.cauldron = null;
  const world = state.world;
  const { w, h, data } = world.physique;
  const cells = world.cellState.data;
  for (let y = c.y0; y <= c.y1; y++) for (let x = c.x0; x <= c.x1; x++) if (data[y * w + x] === OIL) { data[y * w + x] = EMPTY; cells[y * w + x] = 0; }
  let left = CAULDRON_OIL;
  for (let y = c.y1 + 1; y < h && left > 0; y++) {
    for (let x = c.x0 + 1; x < c.x1 && left > 0; x++) {
      const i = y * w + x;
      if (!free(state, i)) continue;
      data[i] = left % 5 === 0 ? FIRE : OIL; // boiling oil, already burning here and there
      cells[i] = 0;
      left--;
    }
  }
  wakeRect(world, c.x0 - 1, c.y0, c.x1 + 1, c.y1 + 12);
  if (gate) {
    // Flames on the wood of the gate (drawbridge, portcullis), where the oil lands.
    for (let y = gate.y0; y <= gate.y1; y++) {
      for (let x = gate.x0; x <= gate.x1; x += 2) {
        const i = y * w + x;
        if (y + 1 < h && data[i + w] === WOOD && data[i] === EMPTY) { data[i] = FIRE; cells[i] = 0; }
      }
    }
    wakeRect(world, gate.x0, gate.y0, gate.x1, gate.y1 + 1);
  }
  state.events.push({ type: 'cauldron', x: (c.x0 + c.x1) >> 1, y: c.y1 });
}

/** A reaction blast (fire or lava on the powder) inside the magazine sets the whole magazine off at once. */
function checkMagazine(state: GameState): void {
  const m = state.map.magazine!;
  const q = state.world.blasts;
  let hit = false;
  for (let i = 0; i < q.length && !hit; i += 3) hit = q[i] >= m.x0 - 2 && q[i] <= m.x1 + 2 && q[i + 1] >= m.y0 - 2 && q[i + 1] <= m.y1 + 2;
  if (!hit) return;
  state.map.magazine = null;
  const cx = (m.x0 + m.x1) >> 1;
  const cy = (m.y0 + m.y1) >> 1;
  // It swallows the other blasts within its radius.
  let k = 0;
  for (let i = 0; i < q.length; i += 3) {
    const dx = q[i] - cx;
    const dy = q[i + 1] - cy;
    if (isqrt(dx * dx + dy * dy) <= MAGAZINE_RADIUS) continue;
    q[k] = q[i]; q[k + 1] = q[i + 1]; q[k + 2] = q[i + 2];
    k += 3;
  }
  q.length = k;
  explode(state, cx, cy, MAGAZINE_RADIUS, MAGAZINE_DAMAGE);
}

/** `count` lava bombs thrown in a bell from (cx, y), the blows of `owner`'s team (-1: nobody's). */
export function throwLavaBombs(state: GameState, cx: number, y: number, count: number, owner: number): void {
  const rng = state.world.rng;
  for (let k = 0; k < count; k++) {
    const angle = 45 + nextInt(rng, 91);
    const speed = ((4 + nextInt(rng, 7)) * PFP) >> 1; // 2 to 5 cells per tick
    const x = cx - 12 + nextInt(rng, 25);
    state.projectiles.push({
      x: (x << PFP_SHIFT) + (PFP >> 1), y: (y << PFP_SHIFT) + (PFP >> 1),
      vx: Math.trunc((speed * cosDeg(angle)) / TRIG_ONE), vy: -Math.trunc((speed * sinDeg(angle)) / TRIG_ONE),
      weapon: PROJ_LAVA_BOMB, owner, age: 0, link: -1,
    });
  }
}

/** The volcano erupts: lava bombs thrown in a bell from the crater, and lava overflowing it. */
export function erupt(state: GameState): void {
  const c = state.map.crater;
  if (!c) return;
  const world = state.world;
  const { w, data } = world.physique;
  const cells = world.cellState.data;
  const cx = (c.x0 + c.x1) >> 1;
  let top = c.y0;
  while (top < c.y1 && free(state, top * w + cx)) top++;
  throwLavaBombs(state, cx, top - 2, LAVA_BOMBS, -1);
  // The overflow: lava on top of the crater's, row by row; it spills over the lowest rim (the notch).
  let left = ERUPTION_OVERFLOW;
  for (let y = top - 1; y >= 0 && left > 0; y--) {
    for (let x = c.x0; x <= c.x1 && left > 0; x++) {
      const i = y * w + x;
      if (!free(state, i)) continue;
      data[i] = LAVA;
      cells[i] = 0;
      left--;
    }
  }
  wakeRect(world, c.x0 - 2, 0, c.x1 + 2, c.y1);
  state.events.push({ type: 'eruption', x: cx, y: top });
}
